import { SCRIM, TutorialCoach } from './tutorial-coach';
import {
  FINAL_STEP_ID, nextStep, stepPosition,
  type TutorialChapter, type TutorialContext, type TutorialSignal, type TutorialStep,
} from './tutorial-script';

/**
 * Runs one chapter: decides what is showing, listens for the thing that ends
 * it, and remembers the answer on the server.
 *
 * ## Progress is per account and written as it happens
 *
 * Not at the end, and not in localStorage. A new player is the exact player
 * most likely to reload, switch to a phone, or close the tab in the middle of
 * the hub tour, and every one of those must resume rather than restart. The
 * cost is one small POST per step, which is why `seen` is throttled higher
 * than the rest of the tutorial routes.
 *
 * ## A failed write is not a failed step
 *
 * `markSeen` rejections are swallowed. The step is already finished on screen;
 * refusing to advance because a background write lost its connection would
 * strand the player behind a plaque for a reason that has nothing to do with
 * them. The consequence is that the step may be taught again next session,
 * which is the mild half of the trade.
 *
 * ## Signals may arrive early
 *
 * A player who loots before the tutorial has reached the loot step, or who
 * moves before being told to, has answered the question already. Those
 * signals are recorded and the step is passed over rather than shown — the
 * fastest way to make a tutorial resented is to explain something the player
 * has visibly just done.
 */

export interface TutorialPersistence {
  markSeen: (stepId: string) => Promise<unknown>;
  skip: () => Promise<unknown>;
  complete: () => Promise<unknown>;
}

export interface TutorialRunnerOptions {
  chapter: TutorialChapter;
  container: HTMLElement;
  seen: readonly string[];
  skipped: boolean;
  context: TutorialContext;
  persistence: TutorialPersistence;
  /** Fired when the last step of the last chapter finishes, for a congratulation the runner does not own. */
  onFinished?: () => void;
  /**
   * Fired with a step's id each time that step goes on screen (2026-09-07).
   *
   * A step's `anchor` points at something; it does not make it *reachable*.
   * The in-run bag collapsed to a summary bar on 2026-09-07, and the
   * `insure-item` step asks the player to mark an item — which they cannot do
   * against a closed list. Rather than teach this file about the bag, it says
   * which step is up and the caller opens whatever that step needs.
   */
  onStepShown?: (stepId: string) => void;
}

export class TutorialRunner {
  private readonly coach: TutorialCoach;
  private readonly seen: Set<string>;
  private readonly fired = new Set<TutorialSignal>();
  private context: TutorialContext;
  private current: TutorialStep | null = null;
  private stopped: boolean;
  private paused = false;

  constructor(private readonly options: TutorialRunnerOptions) {
    this.seen = new Set(options.seen);
    this.context = options.context;
    this.stopped = options.skipped;
    this.coach = new TutorialCoach(options.container, {
      onNext: () => this.finishCurrent(),
      onSkip: () => this.skip(),
    }, SCRIM[options.chapter]);
  }

  /** True once the player has skipped or the chapter has run out of steps. */
  get finished(): boolean { return this.stopped; }

  /** Shows the first applicable step. Safe to call again; it will not stack a second plaque. */
  start(): void {
    this.advance();
  }

  /**
   * Holds the plaque back without ending the chapter — the run's opening
   * fly-through, a modal that owns the screen. Anything the tutorial would
   * have said is said the moment control comes back.
   */
  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    if (paused) { this.coach.hide(); return; }
    // A pause hides the plaque but keeps the step, so coming back has to
    // re-show it rather than ask for the next one — `advance()` would see a
    // step already in progress and do nothing, which is how the run's opening
    // fly-through would have swallowed the whole delve chapter.
    if (this.current) this.showCurrent();
    else this.advance();
  }

  /** New facts about the player. Re-picks the step if the current one has become irrelevant. */
  updateContext(patch: Partial<TutorialContext>): void {
    this.context = { ...this.context, ...patch };
    if (this.current && this.current.when && !this.current.when(this.context)) {
      // Not marked seen: it never applied, and marking it would hide it from a
      // player who later does meet the condition.
      this.current = null;
      this.coach.hide();
    }
    this.advance();
  }

  /**
   * Something happened on the surrounding screen.
   *
   * Ends the current step if this is what it was waiting for; otherwise it is
   * banked, both to unlock steps that only become relevant now (`showAfter`)
   * and to retire ones the player has answered ahead of being asked.
   */
  signal(name: TutorialSignal): void {
    if (this.stopped) return;
    this.fired.add(name);
    if (this.current?.advanceOn === name) { this.finishCurrent(); return; }
    // A step further down the chapter whose action has already happened is
    // retired quietly, so the tour never doubles back to explain it.
    for (const step of this.remaining()) {
      if (step.advanceOn === name && step !== this.current) this.retire(step);
    }
    this.advance();
  }

  private remaining(): TutorialStep[] {
    const out: TutorialStep[] = [];
    for (let guard = 0; guard < 64; guard += 1) {
      const step = nextStep(this.options.chapter, [...this.seen, ...out.map((s) => s.id)], this.context, this.fired);
      if (!step) break;
      out.push(step);
    }
    return out;
  }

  private retire(step: TutorialStep): void {
    if (this.seen.has(step.id)) return;
    this.seen.add(step.id);
    void this.options.persistence.markSeen(step.id).catch(() => {});
    if (step.id === FINAL_STEP_ID) this.finishChapter();
  }

  private finishCurrent(): void {
    const step = this.current;
    if (!step) return;
    this.current = null;
    this.retire(step);
    this.advance();
  }

  private advance(): void {
    if (this.stopped || this.paused) return;
    if (this.current) return;
    const step = nextStep(this.options.chapter, [...this.seen], this.context, this.fired);
    if (!step) {
      // Out of steps that are ready. The chapter is not over — a `showAfter`
      // step may still unlock — so the coach hides rather than disposing.
      this.coach.hide();
      return;
    }
    this.current = step;
    this.showCurrent();
  }

  private showCurrent(): void {
    const step = this.current;
    if (!step) return;
    const { index, total } = stepPosition(step, this.context);
    this.coach.show({ step, index, total });
    this.options.onStepShown?.(step.id);
  }

  private skip(): void {
    this.stopped = true;
    this.current = null;
    this.coach.hide();
    void this.options.persistence.skip().catch(() => {});
  }

  private finishChapter(): void {
    void this.options.persistence.complete().catch(() => {});
    this.options.onFinished?.();
  }

  dispose(): void {
    this.coach.dispose();
  }
}
